import React, { useEffect, useState } from "react";
import { styled, useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next'
import { darken, lighten } from '@mui/material/styles';
import {
    DataGrid, GridColumns, GridColDef, gridClasses, GridCellParams, GridColumnHeaderParams,
    useGridSelector, useGridApiContext, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton,
    gridPageSelector, gridPageCountSelector, GridToolbarDensitySelector, GridRowHeightParams, GridToolbarExport
} from '@mui/x-data-grid';
import { Avatar, MenuItem, Pagination, PaginationItem, Select } from "@mui/material";
import { getDragonsRank } from "../../../commands/rank/getRanks";
import { getMintRanksNo } from "../../../commands/rank/getMintRanksNo";

import clsx from 'clsx';
import Link from "next/link";
import FormControlLabel from '@mui/material/FormControlLabel';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { columns } from "./columns";

//
export type Rank = {
    id: string,
    name: string,
    class: string,
    affinity: string,
    visual: number,
    power: number,
    gene: number,
    total: number,
    label: string,
    visualRank?: number,
    geneRank?: number,
    powerRank?: number,
    totalRank?: number,
    batch: number
}

interface IHash {
    [index: string]: number;
}

interface IMintRanksNo {
    geneScoresHash: IHash,
    visualScoresHash: IHash,
    powerScoresHash: IHash,
    totalScoresHash: IHash
}

const ODD_OPACITY = 0.2;

const getBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6);

const getHoverBackgroundColor = (color: string, mode: string) =>
    mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5);


type RankingCMPProps = {
    className?: string,
    dragons: any,
    reloadDragons: Function
}


function CustomFooterToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarDensitySelector />
        </GridToolbarContainer>
    );
}

function CustomToolbar(props: any) {
    return (
        <GridToolbarContainer>

            <Box>
                <GridToolbarFilterButton className="ranking-filter-button" />

                <FormControlLabel
                    sx={{
                        marginLeft: "-4px",
                        ".MuiFormControlLabel-label": { color: "#f0ccad", fontWeight: 700, fontSize: "13px" }
                    }}
                    control={<Switch disabled={!props.showOnlyMineBtn} sx={{
                        ".MuiSwitch-switchBase.Mui-checked": { color: "white" },
                        ".MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
                            background: "#12d40e"
                        }
                    }} />}
                    label="SHOW MINE ONLY"
                    onChange={props.showUsersChanged}
                    checked={props.showUsers}
                />
            </Box>

            <Box>
                <GridToolbarExport
                    csvOptions={{
                        fileName: 'EternalDragonsRanking',
                        delimiter: ';',
                        utf8WithBom: true,
                    }}
                    sx={{
                        color: "#f0ccad",
                        fontWeight: 700
                    }}
                    {...props} />
            </Box>

        </GridToolbarContainer>
    );
}

function CustomPagination(props: any) {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", width: "170px" }}>
                <Box sx={{ color: "#efcbac", fontSize: "14px", marginRight: "5px", flexShrink: 0 }}>Rows per page: </Box>
                <Box>
                    <Select
                        sx={{
                            height: "30px", color: "#efcbac", fontSize: "15px",
                            ".MuiOutlinedInput-notchedOutline": {
                                border: "1px solid #8e7b68!important"
                            }
                        }}
                        value={props.rowsPerPage}
                        label=""
                        onChange={props.rowsPerPageChanged}>

                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={30}>30</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                </Box>
            </Box>

            <Pagination
                className="ranking-pagination"
                color="primary"
                count={pageCount}
                page={page + 1}
                onChange={(event, value) => apiRef.current.setPage(value - 1)}
                renderItem={(item) => <PaginationItem {...item}
                    className="ranking-pagination" />}

            />
        </>
    );
}



function RankingCMP(props: RankingCMPProps) {

    // Current ranks displayed in table ui!
    const [ranks, setRanks] = useState<Rank[]>();

    // We have 2 types of ranks - "all ranks" and "current user ranks"!
    const [ranksAll, setRanksAll] = useState<Rank[]>([]);
    const [ranksUser, setRanksUser] = useState<Rank[]>([]);
    const [mintRanksNo, setMintRanksNo] = useState<IMintRanksNo>();

    // Show only users dragon ranks!
    const [showUsers, setShowUsers] = React.useState(false);
    const theme = useTheme();

    const showRoadMapHorLine = useMediaQuery(theme.breakpoints.up(1200));
    const { t } = useTranslation();
    const { publicKey } = useWallet();

    // Load once on start!
    useEffect(() => {
        getMintRanks();
    }, []);

    //
    useEffect(() => {

        if (mintRanksNo) {
            getAllRanks();
        }

    }, [mintRanksNo]);

    // Prepare dragons if we have them!
    useEffect(() => {

        if (props.dragons && mintRanksNo) {
            setRanksUser(prepareUserDragonsAsRank(props.dragons));
        }

    }, [props.dragons, mintRanksNo]);

    // Load current user dragons and prepare them if we have connected wallet!
    useEffect(() => {

        if (publicKey) {
            props.reloadDragons(publicKey.toBase58(), false, false);
        }
        else {
            // Reset if user disconnect wallet!
            setShowUsers(false);
            setRanks(ranksAll);
        }

    }, [publicKey]);

    //
    async function getMintRanks() {
        let res = await getMintRanksNo();
        setMintRanksNo(res.data);
    }

    //
    async function getAllRanks() {

        let dragonRes = await getDragonsRank();

        //console.log("ON STRT ", dragonRes)
        if (dragonRes.status >= 0) {

            // Add batch info attribute!
            for (let i = 0; i < dragonRes.data.data.length; i++) {
                let d = dragonRes.data.data[i];
                d.batch = getBatchFromName(d.name);
            }

            addRanksNo(dragonRes.data.data);
            setRanksAll(dragonRes.data.data);
            setRanks(dragonRes.data.data);
        }
    }

    //
    function getBatchFromName(name: string) {
        let dragonNo = name.replace(/.+♯/, '');
        let batch = dragonNo.length < 4 ? 1 : parseInt(dragonNo.charAt(0)) + 1;
        return batch;
    }

    //
    function addRanksNo(data: Rank[]) {
        // Add #x rank to each dragon score!
        for (let i = 0; i < data.length; i++) {
            let rank = data[i];

            rank.geneRank = mintRanksNo?.geneScoresHash[rank.id];
            rank.visualRank = mintRanksNo?.visualScoresHash[rank.id];
            rank.powerRank = mintRanksNo?.powerScoresHash[rank.id];
            rank.totalRank = mintRanksNo?.totalScoresHash[rank.id];
        }
    }

    // We need to prepare dragons returned from backend/getDragons ro ranks object!
    function prepareUserDragonsAsRank(dragons: any) {
        let ranks: Rank[] = [];

        for (let i = 0; i < dragons.length; i++) {
            let d = dragons[i];

            ranks.push({
                id: d.mint,
                name: d.name,
                class: d.class,
                affinity: d.affinity,
                visual: d.score ? d.score.visual : "",
                power: d.score ? d.score.power : "",
                gene: d.score ? d.score.gene : "",
                total: d.score ? d.score.total : "",
                label: d.score ? d.score.label : "",
                visualRank: mintRanksNo?.visualScoresHash[d.mint],
                geneRank: mintRanksNo?.geneScoresHash[d.mint],
                powerRank: mintRanksNo?.powerScoresHash[d.mint],
                totalRank: mintRanksNo?.totalScoresHash[d.mint],
                batch: getBatchFromName(d.name)
            })
        }

        return ranks;
    }

    const showUsersChanged = (event: any) => {
        let checked = event.target.checked;
        setShowUsers(checked);

        if (checked) {
            setRanks(ranksUser);
        }
        else {
            setRanks(ranksAll);
        }
    }

    // Used for pagination "Rows per page"!
    const [rowsPerPage, setRowsPerPage] = useState(15);

    function rowsPerPageChanged(e: any) {
        setRowsPerPage(e.target.value);
    }

    return (
        <Box className={props.className}>
            <Box className="ranking-dragon-container raking-gate-bg-img">

                <Box className="ranking-table" >
                    {ranks &&

                        <DataGrid

                            getRowHeight={({ id, densityFactor }: GridRowHeightParams) => {
                                if ((id as number) % 2 === 0) {
                                    return 100 * densityFactor;
                                }

                                return null;
                            }}

                            columnVisibilityModel={{
                                batch: false, // Dont show batch column! We do this so we can use it in filters but not show it!
                            }}

                            components={{
                                Toolbar: CustomToolbar,
                                Pagination: CustomPagination,
                            }}

                            componentsProps={{
                                columnHeaderFilterIconButton: {
                                    sx: {
                                        backgroundColor: 'red',
                                    }
                                },
                                columnMenu: {
                                    sx: {
                                        backgroundColor: '#935a38',
                                        color: '#efcbac',
                                        fontWeight: 'bold',
                                        fontHeight: '22px'
                                    }
                                },
                                toolbar: {
                                    showUsers,
                                    showUsersChanged,
                                    showOnlyMineBtn: props.dragons && props.dragons.length > 0 && publicKey,
                                    printOptions: { disableToolbarButton: true }
                                },
                                pagination: {
                                    rowsPerPage: rowsPerPage,
                                    rowsPerPageChanged: rowsPerPageChanged
                                }
                            }}
                            rows={ranks}
                            columns={columns}
                            pageSize={rowsPerPage}
                            rowsPerPageOptions={[15, 30, 50, 100]}
                            getRowClassName={(params) => {
                                return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                            }
                            }
                        />
                    }
                </Box>
            </Box>
        </Box>

    );


}



const RankingStyled = styled(RankingCMP)(({ theme }) => ({
    border: "0px",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    flexDirection: "column",
    backgroundImage: "url(/mint/BG_Cave.png)",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "50% 60%",
    padding: "0px 5px 0px 5px",

    ".MuiDataGrid-root": {
        background: "transparent",
        border: "0px",
        borderRadius: "0px"
    },

    ".MuiDataGrid-virtualScroller": {
        //background: "red",
        backgroundImage: "url(/ranking/panel_img.png)",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "50% 60%",

        ".MuiDataGrid-row": {
            background: "#8d5c3355!important"
        },
        ".MuiDataGrid-row.even": {
            background: "#4e2d1455!important"
        },
        ".MuiDataGrid-cell": {
            border: "0px"
        },

        "&::-webkit-scrollbar": {
            width: "12px",
            height: "12px",
        },
        "&::-webkit-scrollbar-track": {
            background: "#c9b29f"
        },
        "&::-webkit-scrollbar-thumb": {
            background: "#9d7f69"
        },
        "&::-webkit-scrollbar-thumb:hover": {
            background: "#9d7f69"
        },
        "&::-webkit-scrollbar-corner": {
            background: "#9d7f69!important"
        }
    },

    ".MuiDataGrid-toolbarContainer": {
        justifyContent: "space-between",

    },

    // Customize pagination position to center!
    ".MuiDataGrid-footerContainer": {
        justifyContent: "center",
        flexWrap: "wrap",
        padding: "5px 38px 5px 38px",
        //background: "red!important",
        borderTop: "0px",
        backgroundImage: "url(/ranking/panel_base.png)",
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
        //backgroundPosition: "100% auto",

        // If we dont do this footer image will be looking weird because of small width!
        ['@media (max-width:1300px)']: {
            backgroundSize: "auto 100%",
        },
    },

    ".MuiDataGrid-columnHeaders": {
        //background: "red!important",
        backgroundImage: "url(/ranking/panel_top.png)",
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
        //backgroundPosition: "100% auto",

        // If we dont do this header image will be looking weird because of small width!
        ['@media (max-width:1300px)']: {
            backgroundSize: "auto 100%",
        },

        ".MuiDataGrid-columnHeader": {
            background: "",
            outline: "none!important"

        },

        ".super-app-theme--header": {
            backgroundColor: "transparent!important"
        },

        ".MuiDataGrid-sortIcon": {
            color: "#00d500!important"
        },

        borderBottom: "0px"
    },

    ".Mui-selected": {
        backgroundColor: '#efcbac!important',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px'
    },

    ".MuiSvgIcon-root": {
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px'
    },

    ".ranking-filter-button": {
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px',
        marginRight: "12px"
    },

    ".ranking-pagination": {
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px'
    },


    ['& .super-app-theme--cell-odd']: {
        backgroundColor: '#935a38',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px'
    },

    ['& .super-app-theme--cell-even']: {
        backgroundColor: '#c48754',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px'
    },

    ['& .super-app-theme--header']: {
        backgroundColor: '#3b2c23',
        color: '#efcbac',
        fontWeight: 600,
        fontHeight: '22px'
    },

    ['& .super-app-theme--footer']: {
        backgroundColor: '#572a1d',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px'
    },

    [`& .${gridClasses.row}.even`]: {
        backgroundColor: '#a46f4c',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px',
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity,
            ),
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
    },

    [`& .${gridClasses.row}.odd`]: {
        backgroundColor: '#cf9969',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px',
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity,
            ),
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
    },


    ".ranking-dragon-container": {
        width: '80%',
        marginTop: '120px',
        position: "relative",
        //backgroundColor: '#534c3e',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px',
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat"

    },
    ".ranking-table": {
        width: '100%',
        height: '80vh',
        color: '#efcbac',
        fontWeight: 'bold',
        fontHeight: '22px',

    },

    ".ranking-avatar": {

        display: 'flex'
    },

    ".ranking-avatar-name": {
        lineHeight: '45px',
        marginLeft: '12px',
        textAlign: 'center'
    },


    ".home-front-dragons-group-img": {
        width: "100%",
        maxWidth: "880px",
        padding: "10px"
    },
    ".home-front-play-and-prosper-img": {
        width: "100%",
        maxWidth: "320px",
        margin: "20px 0px 20px 0px",
        padding: "0 10px 0 10px",
        marginTop: "11%"
    },
    ".home-front-join-comm-img": {
        width: "100%",
        maxWidth: "360px",
        marginBottom: "0px",
        padding: "0 10px 0 10px"
    },
    ".home-front-logo-and-btns": {
        //position: "absolute",
        marginTop: "-60px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "600px"
    },
    ".home-front-text": {
        //position: "absolute",      
        maxWidth: "650px",
        color: "white",
        fontWeight: 700,
        padding: "10px 10px 20px 10px",
        textAlign: "center",
        fontSize: "18px",
        textShadow: "3px 3px 3px #484848"
    },
    ".home-front-btns": {
        transition: "opacity 0.4s",
        "&:hover": {
            opacity: 0.7,
            cursor: "pointer"
        }
    },

    ".home-front-discord-icon": {
        height: "23px",
        paddingRight: "10px"
    },
    ".home-front-social-icon": {
        padding: "4px",
        margin: "7px",
        fontSize: "35px",
        borderRadius: "20px",
        display: "inline-block"
    },
    ".home-whitebox-container": {
        background: "white",
        height: "auto",
        padding: "50px 20px 80px 20px",
        textAlign: "center"
    },
    ".home-whitebox-header": {
        color: "#181f27",
        fontWeight: "700",
        fontSize: "40px",
        paddingBottom: "20px",
        lineHeight: 1.2
    },
    ".home-whitebox-content": {
        color: "#181f27",
        fontWeight: "400",
        padding: "20px",
        fontSize: "19px",
        maxWidth: "600px",
        //border: "2px solid #181f27",
        display: "inline-block"
    },
    ".home-whitebox-btn": {
        display: "inline-block",
        background: "#181f27",
        color: "white",
        borderRadius: "0px",
        fontWeight: 700,
        width: "150px",
        clear: "both",
        position: "relative",
        top: "-12px"
    },
    ".ranking-gate-bg-img": {
        backgroundImage: "url(Home_Page_02_1080p.jpg)",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "50% 60%",
        height: "1000px"
    },
    ".home-hatching-bg-img": {
        backgroundImage: "url(eternaldragons_website_illustration_03_sm.jpg)",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "50% 65%",
        height: "550px"
    },
    ".home-investor-item-container": {
        margin: "auto",
        //padding: "20px",
        maxWidth: "1500px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap"
    },
    ".home-investor-item": {
        lineHeight: "1",
        fontWeight: 700,
        color: "#181f27",
        background: "white",
        display: "inline-block",
        padding: "40px"
    },
    ['@media (max-width:901px)']: {
        ".home-front-dragon-video-cont": {
            overflow: "hidden",
            height: "600px",
            "video": {
                transform: "scale(1.9) translate(-17px, 0px)",
                display: "block"
            }
        }
    },
    ['@media (max-width:520px)']: {
        ".home-front-dragon-video-cont": {
            overflow: "hidden",
            height: "600px",
            "video": {
                transform: "scale(3.4) translate(-17px, 0px)",
                display: "block"
            }
        }
    },
    ['@media (max-width:500px)']: {
        ".home-gate-bg-img": {
            backgroundImage: "url(Home_Page_02_1080p.jpg)",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "50% 60%",
            height: "750px"
        },
        ".home-front-logo-and-btns": {
            marginTop: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
        },
        ".home-front-join-comm-img": {
            width: "100%",
            maxWidth: "290px",
            marginBottom: "0px",
            padding: "0 10px 0 10px"
        }
    },
    ".home-trailer-movie": {
        position: "relative",
        height: 0,
        width: "100%",
        "div": {
            paddingBottom: "56.25%!important", /* 16:9 */
            iframe: {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
            }
        }
    },
    ".cell-overlay": {
        background: "#492c1455"
    }
}));

// Export
export default RankingStyled;
